import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Lock,
  Key,
  Database,
  Server,
  Siren,
  ClipboardCheck,
  Globe,
  FileLock2,
  ArrowUpRight,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';

/**
 * SecurityPage
 * --------------------------------------------------------------
 * Trust + security posture page for Worqly. We never claim a
 * badge we haven't earned — compliance section is explicitly
 * roadmap, not pretend.
 *
 * Visual language matches LandingPage / PricingPage / Features.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function SecurityPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Security"
        description="How Worqly protects your data: encryption in transit and at rest, row-level isolation per workspace, RBAC, audit logs, and our compliance roadmap."
      />
      <Hero />
      <Pillars />
      <PostureSections />
      <Compliance />
      <Reporting />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <ShieldCheck className="h-3 w-3 text-coral" />
              Security & trust
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            Your data, <span className="italic font-serif text-coral">locked down.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Encryption at rest and in transit, row-level tenant isolation, magic-link auth, and
            an honest compliance roadmap. No theatre, no fake badges.
          </motion.p>

          <motion.div variants={FADE_UP} className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href="mailto:security@worqly.io"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Email security@worqly.io
              <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
            >
              Start free
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * PILLARS — 4-up summary
 * ───────────────────────────────────────────────────────────── */

function Pillars() {
  const pillars = [
    { Icon: Lock,     title: 'Encrypted',  body: 'TLS 1.2+ in transit. AES-256 at rest.' },
    { Icon: Database, title: 'Isolated',   body: 'Per-tenant rows enforced at the DB.' },
    { Icon: Key,      title: 'Secure sign-in', body: 'Google, password, or magic link. Short-lived JWTs.' },
    { Icon: Siren,    title: 'Audited',    body: 'Every action logged. Every export keyed.' },
  ];
  return (
    <section className="py-12 lg:py-14 border-y border-ink/8 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] backdrop-blur-md">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5"
        >
          {pillars.map((p) => (
            <motion.li
              key={p.title}
              variants={FADE_UP}
              className="rounded-3xl p-5 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                <p.Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-[16px] font-semibold text-ink dark:text-white">{p.title}</h3>
              <p className="mt-1 text-[13px] text-ink/65 dark:text-white/65 leading-relaxed">{p.body}</p>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * POSTURE SECTIONS — alternating left/right cards
 * ───────────────────────────────────────────────────────────── */

type Posture = {
  id: string;
  Icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  bullets: string[];
  mockTitle: string;
  mockLines: Array<{ label: string; value: string; tag?: string }>;
};

const POSTURES: Posture[] = [
  {
    id: 'isolation',
    Icon: Database,
    eyebrow: 'Data isolation',
    title: <>Multi-tenant with <span className="italic font-serif text-coral">row-level security.</span></>,
    body: 'Every workspace is a tenant. Every row in every table carries that tenant id. Postgres Row-Level Security policies enforce isolation at the database, not just the application — so a query that "forgets" to filter still returns zero rows from another workspace.',
    bullets: [
      'Postgres RLS on every tenant table',
      'Tenant id verified server-side on every request',
      'Cross-tenant joins blocked at the policy layer',
      'Per-tenant key prefix on object storage',
    ],
    mockTitle: 'postgres / rls policy',
    mockLines: [
      { label: 'invoices.tenant_id',  value: 'auth.tenant_id()', tag: 'enforced' },
      { label: 'clients.tenant_id',   value: 'auth.tenant_id()', tag: 'enforced' },
      { label: 'employees.tenant_id', value: 'auth.tenant_id()', tag: 'enforced' },
      { label: 'payslips.tenant_id',  value: 'auth.tenant_id()', tag: 'enforced' },
    ],
  },
  {
    id: 'auth',
    Icon: Key,
    eyebrow: 'Authentication',
    title: <>Sign in your way. <span className="italic font-serif text-coral">Credentials never stored raw.</span></>,
    body: 'Sign in with a Google account, an email and password, or a single-use magic link. Passwords are stored only as salted bcrypt hashes — never in plaintext. Each session is a short-lived JWT signed with a per-environment key; sessions rotate and revocation is one click.',
    bullets: [
      'Google, password, or magic-link sign-in',
      'Passwords stored as bcrypt hashes, never plaintext',
      'Short-lived JWTs with session rotation + revocation',
      'Turnstile bot protection on auth routes',
    ],
    mockTitle: 'auth / session',
    mockLines: [
      { label: 'Method',          value: 'password',       tag: 'verified' },
      { label: 'Issued at',       value: '2026-05-22 09:14 UTC' },
      { label: 'Expires in',      value: '50 minutes',     tag: 'active' },
      { label: 'Refresh binding', value: 'device · macOS' },
    ],
  },
  {
    id: 'storage',
    Icon: FileLock2,
    eyebrow: 'Storage',
    title: <>Files on <span className="italic font-serif text-coral">our own encrypted disk.</span></>,
    body: 'PDFs, receipts, payslips, and uploads are stored on the servers we run the app on — inside an encrypted volume, not a third-party bucket. Every key is prefixed with the tenant id, uploads pass through our server (never direct from browser to bucket), and monitoring screenshots are only ever served through permission-checked API routes. Other file links are long, unguessable HTTPS URLs: they are not signed and do not expire, so treat a file link you share as the file itself.',
    bullets: [
      'Tenant-prefixed object keys',
      'Server-mediated uploads (no direct PUT)',
      'Screenshots proxied through authorised API routes',
      'Encrypted volume at rest, TLS in transit',
    ],
    mockTitle: 'storage / receipt-9831.pdf',
    mockLines: [
      { label: 'Store',   value: 'self-hosted disk' },
      { label: 'Key',     value: 'expenses/tenant_4a9c/.../9831.pdf' },
      { label: 'Link',    value: 'unsigned URL, no expiry' },
      { label: 'At rest', value: 'AES-256 volume', tag: 'encrypted' },
    ],
  },
  {
    id: 'infrastructure',
    Icon: Server,
    eyebrow: 'Infrastructure',
    title: <>Self-hosted on <span className="italic font-serif text-coral">infrastructure we run.</span></>,
    body: 'The site, the app, the API and the database all run on dedicated Linux servers we administer ourselves — no managed platform in between. Application files and secrets sit on an encrypted volume; PostgreSQL listens on localhost only and the app connects with a role that cannot bypass row-level security. Backups today are a nightly full database dump kept for 14 days on the same server — there is no point-in-time recovery and no offsite replica yet. Stripe handles every cent — we never store a PAN.',
    bullets: [
      'Dedicated servers (Ubuntu 24.04), single region',
      'PostgreSQL 17, localhost-only, RLS-enforcing role',
      'Nightly database dump, kept 14 days',
      'No PITR and no offsite copy yet — both planned',
      'Stripe PCI for all card data',
    ],
    mockTitle: 'platform / stack',
    mockLines: [
      { label: 'Web + API', value: 'self-hosted (nginx + Node)' },
      { label: 'Database',  value: 'PostgreSQL 17 (localhost)', tag: 'rls on' },
      { label: 'Backups',   value: 'nightly dump · 14 days' },
      { label: 'Payments',  value: 'Stripe',                    tag: 'pci' },
    ],
  },
  {
    id: 'incident-response',
    Icon: Siren,
    eyebrow: 'Incident response',
    title: <>If something breaks, <span className="italic font-serif text-coral">we tell you.</span></>,
    // Deliberately states what a small team can actually honour. A "24/7
    // on-call rotation" implies staffed shifts; committing to that and missing
    // it in a real incident is worse than saying plainly what we do.
    body: 'We are a small team, so we will not pretend to a staffed 24/7 rota. Our systems are monitored automatically and we respond as fast as we can, in business hours as a rule. Customer-impacting incidents go on the public status page, and we will always tell you what happened rather than quietly fix it. Security incidents trigger named-customer notification within 72 hours.',
    bullets: [
      'Automated monitoring; best-effort response, business hours',
      'Public status page (worqly.io/status)',
      'Incidents disclosed on the status page, not hidden',
      '72-hour customer notification (security)',
      'A written explanation after anything customer-impacting',
      'Security disclosures: security@worqly.io',
    ],
    mockTitle: 'worqly.io/status',
    mockLines: [
      { label: 'API',         value: 'Operational',  tag: 'healthy' },
      { label: 'Dashboard',   value: 'Operational',  tag: 'healthy' },
      { label: 'Payments',    value: 'Operational',  tag: 'healthy' },
      { label: 'Email',       value: 'Operational',  tag: 'healthy' },
    ],
  },
];

function PostureSections() {
  return (
    <>
      {POSTURES.map((p, i) => (
        <PostureBlock key={p.id} posture={p} index={i} />
      ))}
    </>
  );
}

function PostureBlock({ posture, index }: { posture: Posture; index: number }) {
  const flip = index % 2 === 1;
  const tinted = index % 2 === 1;
  return (
    <section
      id={posture.id}
      className={
        'py-20 lg:py-24 scroll-mt-24 ' +
        (tinted
          ? 'bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5'
          : '')
      }
    >
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className={
            'grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ' +
            (flip ? 'lg:[&>*:first-child]:order-2' : '')
          }
        >
          <div className="lg:col-span-6">
            <motion.div variants={FADE_UP} className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                <posture.Icon className="h-5 w-5" />
              </span>
              <Eyebrow className="!text-coral">{posture.eyebrow}</Eyebrow>
            </motion.div>
            <motion.h2
              variants={FADE_UP}
              className="mt-4 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
            >
              {posture.title}
            </motion.h2>
            <motion.p
              variants={FADE_UP}
              className="mt-4 text-[15px] text-ink/65 dark:text-white/65 leading-relaxed max-w-xl"
            >
              {posture.body}
            </motion.p>
            <motion.ul variants={FADE_UP} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {posture.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-coral/12 text-coral-700 dark:bg-coral/20 dark:text-coral-300 shrink-0">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-[13.5px] text-ink/75 dark:text-white/75 leading-snug">{b}</span>
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div variants={FADE_UP} className="lg:col-span-6">
            <PostureMock title={posture.mockTitle} lines={posture.mockLines} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function PostureMock({
  title,
  lines,
}: {
  title: string;
  lines: Array<{ label: string; value: string; tag?: string }>;
}) {
  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-8 bg-coral/15 blur-3xl rounded-full -z-10" />
      <div className="relative rounded-[28px] bg-white/85 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-white/60 dark:ring-white/10 shadow-card-lg overflow-hidden p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="size-2.5 rounded-full bg-rose-400" />
          <span className="size-2.5 rounded-full bg-amber-400" />
          <span className="size-2.5 rounded-full bg-lime-neon" />
          <span className="ml-3 text-[11px] font-mono text-ink/45 dark:text-white/45 truncate">
            {title}
          </span>
        </div>
        <ul className="space-y-2">
          {lines.map((l) => (
            <li
              key={l.label}
              className="flex items-center justify-between gap-3 rounded-2xl bg-pearl-100 dark:bg-white/[0.04] px-4 py-3 ring-1 ring-ink/5 dark:ring-white/5"
            >
              <div className="min-w-0">
                <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-ink/55 dark:text-white/55">
                  {l.label}
                </p>
                <p className="mt-0.5 text-[13.5px] font-mono text-ink dark:text-white truncate">
                  {l.value}
                </p>
              </div>
              {l.tag && (
                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-lime-neon text-ink text-[10px] font-bold uppercase tracking-[0.10em]">
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  {l.tag}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * COMPLIANCE ROADMAP
 * ───────────────────────────────────────────────────────────── */

function Compliance() {
  const items: Array<{
    Icon: React.ComponentType<{ className?: string }>;
    name: string;
    state: 'In progress' | 'Committed' | 'Implemented' | 'Not certified';
    body: string;
  }> = [
    {
      Icon: ShieldCheck,
      name: 'SOC 2 Type II',
      // Was "In progress … report available under NDA on request", which would
      // not survive the first buyer who asked for the report. No audit has been
      // started; the underlying controls are real and can be described honestly
      // on their own merits.
      state: 'Not certified',
      body: 'We are not SOC 2 certified and no audit is under way — if that is a hard requirement for you, say so early and we will tell you straight where we are. The controls an audit looks for are largely in place already: database-enforced tenant isolation, least-privilege database roles, an audit log, 2FA, and encryption at rest. We are happy to walk your security reviewer through any of it.',
    },
    {
      Icon: Globe,
      name: 'GDPR (EU)',
      state: 'Committed',
      body: 'We sign a DPA before any production workload, and workspace deletion runs a hard purge after a 30-day grace window. Self-serve per-employee data export and EU region hosting are planned, not built — ask us and we handle those requests manually today.',
    },
    {
      Icon: FileLock2,
      name: 'CCPA (California)',
      state: 'Committed',
      body: 'Right-to-know, right-to-delete, and opt-out of sale (we don\'t sell data) requests honoured within 45 days. Customer-controlled retention windows.',
    },
    {
      Icon: ClipboardCheck,
      name: 'PCI-DSS',
      state: 'Implemented',
      body: 'No card data ever touches Worqly servers. All payment surfaces are Stripe-hosted, putting us in scope only for SAQ A (the lightest tier).',
    },
  ];

  const tone = (state: typeof items[number]['state']) =>
    state === 'Implemented'
      ? 'bg-lime-neon text-ink'
      : state === 'In progress'
        ? 'bg-coral text-white'
        // 'Not certified' must not borrow a badge that reads like progress —
        // it is a plain factual state, styled neutrally.
        : state === 'Not certified'
          ? 'bg-ink/10 dark:bg-white/15 text-ink dark:text-white'
          : 'bg-ink-900 text-white';

  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Compliance</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            We're building toward these.{' '}
            <span className="italic font-serif text-coral">We won't fake the badges.</span>
          </motion.h2>
          <motion.p variants={FADE_UP} className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl">
            Worqly is a young product. Some compliance work is finished, some is in audit, some is a
            written commitment we plan to deliver in 2026. We label each one honestly.
          </motion.p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
        >
          {items.map((it) => (
            <motion.li
              key={it.name}
              variants={FADE_UP}
              className="rounded-3xl p-6 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                  <it.Icon className="h-5 w-5" />
                </span>
                <span className={'inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.10em] ' + tone(it.state)}>
                  {it.state}
                </span>
              </div>
              <h3 className="mt-4 text-[18px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                {it.name}
              </h3>
              <p className="mt-2 text-[13.5px] text-ink/65 dark:text-white/65 leading-relaxed">
                {it.body}
              </p>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * REPORTING / DISCLOSURE
 * ───────────────────────────────────────────────────────────── */

function Reporting() {
  return (
    <section className="py-16 lg:py-20 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-7 lg:p-10"
        >
          <motion.div variants={FADE_UP} className="flex items-start gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shrink-0">
              <Siren className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="text-[22px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                Reporting a vulnerability
              </h3>
              <p className="mt-2 text-[14.5px] text-ink/70 dark:text-white/70 leading-relaxed">
                If you believe you've found a security issue, please email{' '}
                <a href="mailto:security@worqly.io" className="font-semibold text-coral hover:underline">
                  security@worqly.io
                </a>{' '}
                with details. We respond within one business day, validate within five, and
                disclose responsibly. We do not pursue good-faith researchers.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="mailto:security@worqly.io"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
                >
                  Email security@worqly.io
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/status"
                  className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-ink/70 hover:text-coral dark:text-white/70 dark:hover:text-coral-400 transition-colors px-2 h-11"
                >
                  Visit status page
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FOOTER CTA
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Trusted by design</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Built so the answer to "is it secure?" is <em className="font-serif">yes,</em> not "let me check."
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Flexible sign-in, row-level isolation, encrypted at rest, audited end-to-end. Start
                free and inspect everything.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:security@worqly.io"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                Talk to security
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
